import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import * as Sentry from '@sentry/react'
import './index.css'
import './i18n'
import App from './App.tsx'
import { env } from '@/config/env'
import { ErrorBoundary } from '@/components/shared/ErrorBoundary'
import type { AppError } from '@/api/errors'

/**
 * Story E01.12 — Sentry initialization (D-05).
 *
 * Init MUST run before any React render so the SDK is in place when
 * `componentDidCatch` (in our ErrorBoundary) calls `captureException`.
 *
 * `enabled: false` when no DSN is set: this is the expected pre-D-05
 * state. The SDK still installs its hub so `captureException` returns
 * a no-op event-id without throwing.
 *
 * `beforeSend` filters out classes of errors that are not bugs but
 * normal user-flow outcomes — auth lapses, permission denials, network
 * blips. Without this, every offline second would generate noise.
 */
Sentry.init({
  dsn: env.sentryDsn,
  environment: env.mode,
  enabled: !!env.sentryDsn,
  release: env.appVersion,
  tracesSampleRate: 0.1,
  // Replays add ~30 KB; keeping session replays at 0 keeps the budget
  // honest, while errors-only replay (1.0) gives us reproductions when
  // something blows up in prod.
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: 1.0,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({ maskAllText: false, blockAllMedia: true }),
  ],
  beforeSend: (event, hint) => {
    const err = hint?.originalException as AppError | undefined
    if (err && typeof err === 'object' && 'kind' in err) {
      if (err.kind === 'auth' || err.kind === 'forbidden' || err.kind === 'network') {
        return null
      }
    }
    return event
  },
})

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    {/* Root ErrorBoundary (AC-02). Anything that escapes feature-level
        boundaries lands here and renders the translated fallback. */}
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  </StrictMode>,
)
